/**
 * SurveyRenderer.jsx  —  Metadata-driven survey form
 * Supports: text, textarea, number, email, phone, url, select, yesno,
 *           radio, checkbox, date, date_parts, time, datetime,
 *           rating, nps, slider, file, signature, matrix, ranking, section
 */

import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Fetchexecutequery } from "../../../services/process/BRS1/brsService7";
import { CreateData } from "../../../services/process/process_data/process_data";
import sandhi from '../../../assets/images/pontis.png';

// ────────────────────────────────────────────────────────────────────────────
// Individual question components
// ────────────────────────────────────────────────────────────────────────────

function TextInput({ question, value, onChange, hasError }) {
  const cfg = question.config || {};
  return (
    <input
      type="text"
      className={`sr-input ${hasError ? "sr-invalid" : ""}`}
      placeholder={cfg.placeholder || ""}
      value={value || ""}
      maxLength={cfg.max_length || undefined}
      minLength={cfg.min_length || undefined}
      onChange={e => onChange(question.id, e.target.value)}
      required={!!question.is_required}
    />
  );
}

function TextareaInput({ question, value, onChange, hasError }) {
  const cfg = question.config || {};
  return (
    <textarea
      className={`sr-input sr-textarea ${hasError ? "sr-invalid" : ""}`}
      placeholder={cfg.placeholder || ""}
      value={value || ""}
      maxLength={cfg.max_length || undefined}
      rows={cfg.rows || 4}
      onChange={e => onChange(question.id, e.target.value)}
      required={!!question.is_required}
    />
  );
}

function NumberInput({ question, value, onChange, hasError }) {
  const cfg = question.config || {};
  return (
    <input
      type="number"
      className={`sr-input ${hasError ? "sr-invalid" : ""}`}
      value={value || ""}
      min={cfg.min_value ?? undefined}
      max={cfg.max_value ?? undefined}
      step={cfg.step || 1}
      placeholder={cfg.placeholder || "Enter a number"}
      onChange={e => onChange(question.id, e.target.value)}
      required={!!question.is_required}
    />
  );
}

function EmailInput({ question, value, onChange, hasError }) {
  const cfg = question.config || {};
  return (
    <input
      type="email"
      className={`sr-input ${hasError ? "sr-invalid" : ""}`}
      placeholder={cfg.placeholder || "email@example.com"}
      value={value || ""}
      onChange={e => onChange(question.id, e.target.value)}
      required={!!question.is_required}
    />
  );
}

function PhoneInput({ question, value, onChange, hasError }) {
  const cfg = question.config || {};
  const phoneDigits = (value || "").split("|")[1] || "";
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <input
        type="text"
        className={`sr-input ${hasError ? "sr-invalid" : ""}`}
        style={{ maxWidth: 90 }}
        value={(value || "").split("|")[0] || cfg.country_code || "+91"}
        onChange={e => {
          const parts = (value || "").split("|");
          onChange(question.id, `${e.target.value}|${parts[1] || ""}`);
        }}
        placeholder="+91"
      />
      <input
        type="tel"
        className={`sr-input ${hasError ? "sr-invalid" : ""}`}
        style={{ flex: 1 }}
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={cfg.max_digits || undefined}
        value={phoneDigits}
        onChange={e => {
          const parts = (value || "").split("|");
          const digitsOnly = e.target.value.replace(/\D/g, "");
          onChange(question.id, `${parts[0] || cfg.country_code || "+91"}|${digitsOnly}`);
        }}
        placeholder={cfg.placeholder || "Enter phone number"}
        required={!!question.is_required}
      />
    </div>
  );
}

function UrlInput({ question, value, onChange, hasError }) {
  const cfg = question.config || {};
  return (
    <input
      type="url"
      className={`sr-input ${hasError ? "sr-invalid" : ""}`}
      placeholder={cfg.placeholder || "https://"}
      value={value || ""}
      onChange={e => onChange(question.id, e.target.value)}
      required={!!question.is_required}
    />
  );
}

function SelectInput({ question, value, onChange, hasError }) {
  const cfg = question.config || {};
  return (
    <select
      className={`sr-input sr-select ${hasError ? "sr-invalid" : ""}`}
      value={value || ""}
      onChange={e => onChange(question.id, e.target.value)}
      required={!!question.is_required}
    >
      <option value="">{cfg.placeholder || "-- Select an option --"}</option>
      {question.options?.map(opt => (
        <option key={opt.id} value={opt.option_value}>{opt.option_label}</option>
      ))}
      {cfg.allow_other && <option value="__other__">Other…</option>}
    </select>
  );
}

function YesNoInput({ question, value, onChange }) {
  const cfg   = question.config || {};
  const style = cfg.style || "buttons";
  const yesLabel = cfg.label_yes || "Yes";
  const noLabel  = cfg.label_no  || "No";

  if (style === "toggle") {
    return (
      <div className="sr-toggle-wrap">
        <button
          type="button"
          className={`sr-toggle-btn ${value === "yes" ? "active" : ""}`}
          onClick={() => onChange(question.id, "yes")}
        >{yesLabel}</button>
        <button
          type="button"
          className={`sr-toggle-btn ${value === "no" ? "active" : ""}`}
          onClick={() => onChange(question.id, "no")}
        >{noLabel}</button>
      </div>
    );
  }

  if (style === "radio") {
    return (
      <div className="sr-options-group">
        {[{ val: "yes", label: yesLabel }, { val: "no", label: noLabel }].map(o => (
          <label key={o.val} className={`sr-option-label ${value === o.val ? "selected" : ""}`}>
            <input type="radio" name={`q_${question.id}`} value={o.val}
              checked={value === o.val}
              onChange={() => onChange(question.id, o.val)}
              required={!!question.is_required} />
            <span className="sr-option-indicator" />
            {o.label}
          </label>
        ))}
      </div>
    );
  }

  // default: buttons
  return (
    <div className="sr-yesno-wrap">
      <button
        type="button"
        className={`sr-yesno-btn yes ${value === "yes" ? "active" : ""}`}
        onClick={() => onChange(question.id, "yes")}
      >
        <span className="sr-yesno-icon">✓</span> {yesLabel}
      </button>
      <button
        type="button"
        className={`sr-yesno-btn no ${value === "no" ? "active" : ""}`}
        onClick={() => onChange(question.id, "no")}
      >
        <span className="sr-yesno-icon">✗</span> {noLabel}
      </button>
    </div>
  );
}

function RadioInput({ question, value, onChange }) {
  const cfg = question.config || {};
  const layout = cfg.layout || "vertical";
  return (
    <div className={`sr-options-group ${layout !== "vertical" ? "horizontal" : ""}`}>
      {question.options?.map(opt => (
        <label key={opt.id} className={`sr-option-label ${value === opt.option_value ? "selected" : ""}`}>
          <input type="radio" name={`q_${question.id}`} value={opt.option_value}
            checked={value === opt.option_value}
            onChange={() => onChange(question.id, opt.option_value)}
            required={!!question.is_required} />
          <span className="sr-option-indicator" />
          {opt.option_label}
        </label>
      ))}
      {cfg.allow_other && (
        <label className={`sr-option-label ${value === "__other__" ? "selected" : ""}`}>
          <input type="radio" name={`q_${question.id}`} value="__other__"
            checked={value === "__other__"}
            onChange={() => onChange(question.id, "__other__")} />
          <span className="sr-option-indicator" />
          Other…
        </label>
      )}
    </div>
  );
}

function CheckboxInput({ question, value = [], onChange }) {
  const cfg = question.config || {};
  const toggle = (optValue) => {
    const current = Array.isArray(value) ? value : [];
    const next = current.includes(optValue)
      ? current.filter(v => v !== optValue)
      : [...current, optValue];
    onChange(question.id, next);
  };
  const layout = cfg.layout || "vertical";
  return (
    <div className={`sr-options-group ${layout !== "vertical" ? "horizontal" : ""}`}>
      {question.options?.map(opt => (
        <label key={opt.id} className={`sr-option-label checkbox ${(value || []).includes(opt.option_value) ? "selected" : ""}`}>
          <input type="checkbox" value={opt.option_value}
            checked={(value || []).includes(opt.option_value)}
            onChange={() => toggle(opt.option_value)} />
          <span className="sr-option-indicator check" />
          {opt.option_label}
        </label>
      ))}
      {cfg.allow_other && (
        <label className={`sr-option-label checkbox ${(value || []).includes("__other__") ? "selected" : ""}`}>
          <input type="checkbox" value="__other__"
            checked={(value || []).includes("__other__")}
            onChange={() => toggle("__other__")} />
          <span className="sr-option-indicator check" />
          Other…
        </label>
      )}
    </div>
  );
}

function DateInput({ question, value, onChange, hasError }) {
  const cfg = question.config || {};
  return (
    <input
      type="date"
      className={`sr-input ${hasError ? "sr-invalid" : ""}`}
      value={value || (cfg.default_today ? new Date().toISOString().slice(0, 10) : "")}
      min={cfg.min_date || undefined}
      max={cfg.max_date || undefined}
      onChange={e => onChange(question.id, e.target.value)}
      required={!!question.is_required}
    />
  );
}

function DatePartsInput({ question, value, onChange, hasError }) {
  const cfg = question.config || {};
  const parts = (value || "||").split("|");
  const [day, month, year] = [parts[0], parts[1], parts[2]];

  const showDay   = cfg.show_day   !== false;
  const showMonth = cfg.show_month !== false;
  const showYear  = cfg.show_year  !== false;
  const minYear   = cfg.min_year   || 1900;
  const maxYear   = cfg.max_year   || new Date().getFullYear();
  const monthFmt  = cfg.month_format || "long";

  const months = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(2000, i, 1);
    return {
      value: String(i + 1).padStart(2, "0"),
      label: monthFmt === "numeric"
        ? String(i + 1).padStart(2, "0")
        : d.toLocaleString("en-US", { month: monthFmt }),
    };
  });

  const update = (d, m, y) => onChange(question.id, `${d}|${m}|${y}`);

  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
      {showDay && (
        <div style={{ flex: "0 0 80px" }}>
          <label className="sr-sub-label">Day</label>
          <select className={`sr-input sr-select ${hasError ? "sr-invalid" : ""}`}
            value={day || ""}
            onChange={e => update(e.target.value, month, year)}
            required={!!question.is_required && showDay}>
            <option value="">DD</option>
            {Array.from({ length: 31 }, (_, i) => (
              <option key={i + 1} value={String(i + 1).padStart(2, "0")}>
                {String(i + 1).padStart(2, "0")}
              </option>
            ))}
          </select>
        </div>
      )}
      {showMonth && (
        <div style={{ flex: "1 1 130px" }}>
          <label className="sr-sub-label">Month</label>
          <select className={`sr-input sr-select ${hasError ? "sr-invalid" : ""}`}
            value={month || ""}
            onChange={e => update(day, e.target.value, year)}
            required={!!question.is_required && showMonth}>
            <option value="">Month</option>
            {months.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
      )}
      {showYear && (
        <div style={{ flex: "0 0 100px" }}>
          <label className="sr-sub-label">Year</label>
          <select className={`sr-input sr-select ${hasError ? "sr-invalid" : ""}`}
            value={year || ""}
            onChange={e => update(day, month, e.target.value)}
            required={!!question.is_required && showYear}>
            <option value="">YYYY</option>
            {Array.from(
              { length: maxYear - minYear + 1 },
              (_, i) => maxYear - i
            ).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

function TimeInput({ question, value, onChange, hasError }) {
  const cfg  = question.config || {};
  const type = cfg.format_24h ? "time" : "time";
  return (
    <input
      type={type}
      className={`sr-input ${hasError ? "sr-invalid" : ""}`}
      value={value || ""}
      step={cfg.show_seconds ? 1 : 60}
      onChange={e => onChange(question.id, e.target.value)}
      required={!!question.is_required}
    />
  );
}

function DateTimeInput({ question, value, onChange, hasError }) {
  const cfg = question.config || {};
  return (
    <input
      type="datetime-local"
      className={`sr-input ${hasError ? "sr-invalid" : ""}`}
      value={value || ""}
      min={cfg.min_datetime || undefined}
      max={cfg.max_datetime || undefined}
      onChange={e => onChange(question.id, e.target.value)}
      required={!!question.is_required}
    />
  );
}

function RatingInput({ question, value, onChange }) {
  const cfg      = question.config || {};
  const max      = Number(cfg.max_stars || 5);
  const iconType = cfg.icon || "star";
  const icons    = { star: "★", heart: "♥", thumb: "👍" };
  const icon     = icons[iconType] || "★";
  const [hovered, setHovered] = useState(0);

  return (
    <div>
      <div className="sr-rating-group">
        {Array.from({ length: max }, (_, i) => i + 1).map(n => (
          <button
            key={n}
            type="button"
            className={`sr-star ${(hovered || Number(value)) >= n ? "active" : ""}`}
            onMouseEnter={() => setHovered(n)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => onChange(question.id, n)}
            aria-label={`${n} ${iconType}`}
          >
            {icon}
          </button>
        ))}
        {Number(value) > 0 && (
          <span className="sr-rating-value">{value} / {max}</span>
        )}
      </div>
      {cfg.show_labels && (
        <div className="sr-rating-labels">
          <span>{cfg.label_low || "Poor"}</span>
          <span>{cfg.label_high || "Excellent"}</span>
        </div>
      )}
    </div>
  );
}

function NpsInput({ question, value, onChange }) {
  const cfg      = question.config || {};
  const showBands = cfg.show_groups !== false;
  const selected  = value !== undefined && value !== "" ? Number(value) : null;

  const bandFor = (n) => {
    if (n <= 6) return "detractor";
    if (n <= 8) return "passive";
    return "promoter";
  };

  return (
    <div className="sr-nps-wrap">
      <div className="sr-nps-scale">
        {Array.from({ length: 11 }, (_, i) => i).map(n => (
          <button
            key={n}
            type="button"
            className={`sr-nps-btn ${bandFor(n)} ${selected === n ? "active" : ""}`}
            onClick={() => onChange(question.id, n)}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="sr-nps-labels">
        <span>{cfg.label_low || "Not likely"}</span>
        <span>{cfg.label_high || "Extremely likely"}</span>
      </div>
      {showBands && (
        <div className="sr-nps-bands">
          <span className="detractor">0–6 Detractors</span>
          <span className="passive">7–8 Passives</span>
          <span className="promoter">9–10 Promoters</span>
        </div>
      )}
    </div>
  );
}

function SliderInput({ question, value, onChange }) {
  const cfg      = question.config || {};
  const min      = Number(cfg.min_value ?? 0);
  const max      = Number(cfg.max_value ?? 10);
  const step     = Number(cfg.step || 1);
  const showVal  = cfg.show_value !== false;
  const current  = value !== undefined && value !== "" ? Number(value) : min;

  return (
    <div className="sr-slider-wrap">
      {cfg.label_low && <span className="sr-slider-label-low">{cfg.label_low}</span>}
      <div className="sr-slider-inner">
        <input
          type="range"
          className="sr-slider"
          min={min}
          max={max}
          step={step}
          value={current}
          onChange={e => onChange(question.id, Number(e.target.value))}
          required={!!question.is_required}
        />
        {showVal && (
          <div className="sr-slider-value">{current}</div>
        )}
      </div>
      {cfg.label_high && <span className="sr-slider-label-high">{cfg.label_high}</span>}
      <div className="sr-slider-track-labels">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}

function FileInput({ question, value, onChange, hasError }) {
  const cfg = question.config || {};
  return (
    <label className={`sr-file-label ${hasError ? "sr-invalid" : ""}`}>
      <input
        type="file"
        accept={cfg.allowed_types || undefined}
        multiple={!!cfg.multiple}
        style={{ display: "none" }}
        onChange={e => {
          const files = Array.from(e.target.files).map(f => f.name).join(", ");
          onChange(question.id, files);
        }}
        required={!!question.is_required && !value}
      />
      <span className="sr-file-btn">
        📎 {value ? value : (cfg.multiple ? "Choose files…" : "Choose file…")}
      </span>
      {cfg.allowed_types && (
        <span className="sr-file-hint">
          Allowed: {cfg.allowed_types}
          {cfg.max_size_mb ? ` · Max ${cfg.max_size_mb}MB` : ""}
        </span>
      )}
    </label>
  );
}

function SignatureInput({ question, value, onChange, hasError }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setDrawing(true);
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!drawing) return;
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.lineWidth = 2;
    ctx.lineCap   = "round";
    ctx.strokeStyle = "#1a2a4a";
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = () => {
    setDrawing(false);
    const dataUrl = canvasRef.current.toDataURL();
    onChange(question.id, dataUrl);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange(question.id, "");
  };

  return (
    <div className={`sr-signature-wrap ${hasError ? "sr-invalid" : ""}`}>
      <canvas
        ref={canvasRef}
        width={500}
        height={150}
        className="sr-signature-canvas"
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
      <div className="sr-signature-actions">
        <span className="sr-signature-hint">Draw your signature above</span>
        <button type="button" className="sr-signature-clear" onClick={clear}>Clear</button>
      </div>
    </div>
  );
}

function MatrixInput({ question, value = {}, onChange, hasError }) {
  const cfg   = question.config || {};
  const multi = !!cfg.allow_multiple;
  const rows  = question.config?.matrix_rows || question.matrix_rows || [];
  const cols  = question.config?.matrix_cols || question.matrix_cols || [];

  const handleCellChange = (rowLabel, colLabel) => {
    const currentVal = value || {};
    if (multi) {
      const rowVals = Array.isArray(currentVal[rowLabel]) ? currentVal[rowLabel] : [];
      const newVals = rowVals.includes(colLabel)
        ? rowVals.filter(v => v !== colLabel)
        : [...rowVals, colLabel];
      onChange(question.id, { ...currentVal, [rowLabel]: newVals });
    } else {
      onChange(question.id, { ...currentVal, [rowLabel]: colLabel });
    }
  };

  const isSelected = (rowLabel, colLabel) => {
    const v = (value || {})[rowLabel];
    return multi ? (Array.isArray(v) && v.includes(colLabel)) : v === colLabel;
  };

  return (
    <div className={`sr-matrix-wrap ${hasError ? "sr-invalid" : ""}`}>
      <table className="sr-matrix-table">
        <thead>
          <tr>
            <th className="sr-matrix-th"></th>
            {cols.map((col, ci) => (
              <th key={ci} className="sr-matrix-th">{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 0 ? "sr-matrix-row-even" : ""}>
              <td className="sr-matrix-row-label">{row}</td>
              {cols.map((col, ci) => (
                <td key={ci} className="sr-matrix-cell">
                  <input
                    type={multi ? "checkbox" : "radio"}
                    name={`matrix_${question.id}_${ri}`}
                    checked={isSelected(row, col)}
                    onChange={() => handleCellChange(row, col)}
                    className="sr-matrix-input"
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RankingInput({ question, value = [], onChange, hasError }) {
  const cfg     = question.config || {};
  const options = question.options || [];
  const [items, setItems]   = useState(() => {
    if (Array.isArray(value) && value.length) return value;
    return options.map(o => o.option_label);
  });
  const [dragging, setDragging] = useState(null);

  const handleDragStart = (idx) => setDragging(idx);
  const handleDragOver  = (e, idx) => {
    e.preventDefault();
    if (dragging === null || dragging === idx) return;
    const newItems = [...items];
    const [moved]  = newItems.splice(dragging, 1);
    newItems.splice(idx, 0, moved);
    setItems(newItems);
    setDragging(idx);
    onChange(question.id, newItems);
  };

  return (
    <div className={`sr-ranking-wrap ${hasError ? "sr-invalid" : ""}`}>
      <p className="sr-ranking-hint">{cfg.instruction || "Drag to rank from most to least preferred"}</p>
      {items.map((item, idx) => (
        <div
          key={item}
          className={`sr-ranking-item ${dragging === idx ? "dragging" : ""}`}
          draggable
          onDragStart={() => handleDragStart(idx)}
          onDragOver={(e) => handleDragOver(e, idx)}
          onDragEnd={() => setDragging(null)}
        >
          <span className="sr-ranking-num">{idx + 1}</span>
          <span className="sr-ranking-label">{item}</span>
          <span className="sr-ranking-handle">⠿</span>
        </div>
      ))}
    </div>
  );
}

function SectionHeading({ question }) {
  const cfg = question.config || {};
  return (
    <div className="sr-section-heading">
      <h3 className="sr-section-title">{question.label}</h3>
      {cfg.description && <p className="sr-section-desc">{cfg.description}</p>}
      {cfg.divider !== false && <div className="sr-section-divider" />}
    </div>
  );
}

// ── Question type → component map ────────────────────────────────────────────
const QUESTION_COMPONENTS = {
  text:       TextInput,
  textarea:   TextareaInput,
  number:     NumberInput,
  email:      EmailInput,
  phone:      PhoneInput,
  url:        UrlInput,
  select:     SelectInput,
  yesno:      YesNoInput,
  radio:      RadioInput,
  checkbox:   CheckboxInput,
  date:       DateInput,
  date_parts: DatePartsInput,
  time:       TimeInput,
  datetime:   DateTimeInput,
  rating:     RatingInput,
  nps:        NpsInput,
  slider:     SliderInput,
  file:       FileInput,
  signature:  SignatureInput,
  matrix:     MatrixInput,
  ranking:    RankingInput,
  section:    SectionHeading,
};

// ── CSS ───────────────────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=DM+Serif+Display&display=swap');

  * { box-sizing: border-box; }

  .sr-page {
    min-height: 100vh;
    background: #f4f1eb;
    padding: 40px 16px 80px;
    font-family: 'DM Sans', sans-serif;
    color: #1a1a2e;
  }

  .sr-card {
    max-width: 680px;
    margin: 0 auto;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 4px 24px rgba(10,35,82,0.10);
    overflow: hidden;
  }

  .sr-header {
    background: linear-gradient(180deg,#f8fbff,#eef5ff);
    color: #0a2352;
    text-align:center;
    padding:30px 40px 35px;
  }


  .sr-header-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(255,255,255,0.15);
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 4px 12px;
    border-radius: 20px;
    margin-bottom: 14px;
  }

  .sr-header h1 {
    font-family: 'DM Serif Display', serif;
    font-size: 28px;
    font-weight: 400;
    margin: 0 0 10px;
    line-height: 1.25;
  }

  .sr-header p {
    font-size: 14px;
    opacity: 0.8;
    margin: 0;
    line-height: 1.6;
  }

  .sr-progress {
    height: 4px;
    background: rgba(255,255,255,0.25);
    border-radius: 0;
    overflow: hidden;
  }

  .sr-progress-bar {
    height: 100%;
    background: #7dd3fc;
    transition: width 0.4s ease;
  }

  .sr-body {
    padding: 32px 40px 40px;
  }

  .sr-question-block {
    margin-bottom: 28px;
    padding-bottom: 28px;
    border-bottom: 1px solid #f0ede6;
    animation: fadeUp 0.3s ease both;
  }

  .sr-question-block:last-of-type {
    border-bottom: none;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .sr-q-label {
    font-size: 15px;
    font-weight: 600;
    color: #1a2a4a;
    margin-bottom: 10px;
    display: flex;
    align-items: flex-start;
    gap: 6px;
    line-height: 1.45;
  }

  .sr-q-num {
    font-size: 11px;
    font-weight: 700;
    color: #7899cc;
    background: #eef3fc;
    padding: 2px 8px;
    border-radius: 20px;
    white-space: nowrap;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .sr-required {
    color: #e05252;
    margin-left: 2px;
    font-size: 13px;
  }

  .sr-input {
    display: block;
    width: 100%;
    padding: 11px 14px;
    border: 1.5px solid #dde0e8;
    border-radius: 10px;
    font-size: 14px;
    font-family: 'DM Sans', sans-serif;
    color: #1a2a4a;
    background: #fafbff;
    transition: border-color 0.2s, box-shadow 0.2s;
    outline: none;
  }

  .sr-input:focus {
    border-color: #4969ab;
    box-shadow: 0 0 0 3px rgba(73,105,171,0.12);
    background: #fff;
  }

  .sr-input.sr-invalid,
  .sr-textarea.sr-invalid,
  .sr-select.sr-invalid,
  .sr-file-label.sr-invalid .sr-file-btn,
  .sr-signature-wrap.sr-invalid .sr-signature-canvas,
  .sr-matrix-wrap.sr-invalid .sr-matrix-table,
  .sr-ranking-wrap.sr-invalid .sr-ranking-item {
    border-color: #ef4444 !important;
    box-shadow: 0 0 0 3px rgba(239,68,68,0.10) !important;
  }

  .sr-question-block.invalid {
    border-bottom-color: #ef4444;
  }

  .sr-textarea {
    resize: vertical;
    min-height: 100px;
  }

  .sr-select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%234969ab' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    padding-right: 36px;
    cursor: pointer;
  }

  .sr-sub-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: #7899cc;
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  /* Yes/No */
  .sr-yesno-wrap {
    display: flex;
    gap: 12px;
  }

  .sr-yesno-btn {
    flex: 1;
    padding: 14px 20px;
    border-radius: 10px;
    border: 2px solid #dde0e8;
    background: #fafbff;
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .sr-yesno-btn.yes:hover, .sr-yesno-btn.yes.active {
    background: #e6f9ef; border-color: #22c55e; color: #15803d;
  }
  .sr-yesno-btn.no:hover, .sr-yesno-btn.no.active {
    background: #fff0f0; border-color: #ef4444; color: #b91c1c;
  }

  .sr-yesno-icon { font-size: 16px; }

  .sr-toggle-wrap {
    display: inline-flex;
    border: 1.5px solid #dde0e8;
    border-radius: 10px;
    overflow: hidden;
  }

  .sr-toggle-btn {
    padding: 10px 28px;
    border: none;
    background: #fafbff;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .sr-toggle-btn:first-child { border-right: 1.5px solid #dde0e8; }

  .sr-toggle-btn.active {
    background: #0a2352;
    color: #fff;
  }

  /* Options (radio / checkbox) */
  .sr-options-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .sr-options-group.horizontal {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 10px;
  }

  .sr-option-label {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 11px 14px;
    border: 1.5px solid #dde0e8;
    border-radius: 10px;
    cursor: pointer;
    font-size: 14px;
    color: #1a2a4a;
    background: #fafbff;
    transition: all 0.18s;
    user-select: none;
    position: relative;
  }

  .sr-option-label:hover {
    border-color: #4969ab;
    background: #f0f4fc;
  }

  .sr-option-label.selected {
    border-color: #4969ab;
    background: #eef3fc;
    color: #0a2352;
  }

  .sr-option-label input[type="radio"],
  .sr-option-label input[type="checkbox"] {
    display: none;
  }

  .sr-option-indicator {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid #c0c8da;
    flex-shrink: 0;
    background: #fff;
    position: relative;
    transition: all 0.18s;
  }

  .sr-option-label.selected .sr-option-indicator {
    border-color: #4969ab;
    background: #4969ab;
  }

  .sr-option-label.selected .sr-option-indicator::after {
    content: '';
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 7px; height: 7px;
    background: #fff;
    border-radius: 50%;
  }

  .sr-option-indicator.check {
    border-radius: 5px;
  }

  .sr-option-label.selected .sr-option-indicator.check::after {
    content: '✓';
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    width: auto; height: auto;
    background: none;
    border-radius: 0;
  }

  /* Rating */
  .sr-rating-group {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
  }

  .sr-star {
    font-size: 30px;
    background: none;
    border: none;
    cursor: pointer;
    color: #d1d5db;
    transition: color 0.15s, transform 0.1s;
    padding: 2px;
    line-height: 1;
  }

  .sr-star.active { color: #f59e0b; }
  .sr-star:hover  { transform: scale(1.15); }

  .sr-rating-value {
    font-size: 13px;
    font-weight: 600;
    color: #7899cc;
    margin-left: 8px;
  }

  .sr-rating-labels {
    display: flex;
    justify-content: space-between;
    margin-top: 4px;
    font-size: 11px;
    color: #9ca3af;
  }

  /* NPS */
  .sr-nps-wrap { width: 100%; }

  .sr-nps-scale {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
  }

  .sr-nps-btn {
    flex: 1;
    min-width: 40px;
    padding: 10px 4px;
    border-radius: 8px;
    border: 1.5px solid #dde0e8;
    background: #fafbff;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.18s;
    text-align: center;
  }

  .sr-nps-btn.detractor:hover, .sr-nps-btn.detractor.active {
    background: #fee2e2; border-color: #f87171; color: #b91c1c;
  }
  .sr-nps-btn.passive:hover, .sr-nps-btn.passive.active {
    background: #fef9c3; border-color: #facc15; color: #854d0e;
  }
  .sr-nps-btn.promoter:hover, .sr-nps-btn.promoter.active {
    background: #dcfce7; border-color: #4ade80; color: #15803d;
  }

  .sr-nps-labels {
    display: flex;
    justify-content: space-between;
    margin-top: 6px;
    font-size: 11px;
    color: #9ca3af;
  }

  .sr-nps-bands {
    display: flex;
    gap: 8px;
    margin-top: 8px;
    font-size: 11px;
    font-weight: 600;
  }

  .sr-nps-bands .detractor { color: #ef4444; }
  .sr-nps-bands .passive   { color: #d97706; }
  .sr-nps-bands .promoter  { color: #16a34a; }

  /* Slider */
  .sr-slider-wrap {
    width: 100%;
  }

  .sr-slider-inner {
    position: relative;
    padding-top: 8px;
  }

  .sr-slider {
    -webkit-appearance: none;
    width: 100%;
    height: 6px;
    border-radius: 4px;
    background: linear-gradient(to right, #4969ab 0%, #4969ab var(--val, 50%), #dde0e8 var(--val, 50%));
    outline: none;
    cursor: pointer;
  }

  .sr-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 22px; height: 22px;
    border-radius: 50%;
    background: #0a2352;
    border: 3px solid #fff;
    box-shadow: 0 2px 6px rgba(10,35,82,0.3);
    cursor: grab;
  }

  .sr-slider-value {
    display: inline-block;
    background: #0a2352;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 20px;
    margin-top: 8px;
  }

  .sr-slider-track-labels {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: #9ca3af;
    margin-top: 4px;
  }

  .sr-slider-label-low,
  .sr-slider-label-high {
    font-size: 12px;
    color: #7899cc;
    font-weight: 500;
  }

  /* File */
  .sr-file-label {
    display: block;
    cursor: pointer;
  }

  .sr-file-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 11px 20px;
    border: 2px dashed #c0c8da;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
    color: #4969ab;
    background: #f7f9ff;
    transition: all 0.2s;
    cursor: pointer;
  }

  .sr-file-label:hover .sr-file-btn {
    border-color: #4969ab;
    background: #eef3fc;
  }

  .sr-file-hint {
    display: block;
    font-size: 11px;
    color: #9ca3af;
    margin-top: 5px;
  }

  /* Signature */
  .sr-signature-wrap {
    border: 1.5px solid #dde0e8;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
  }

  .sr-signature-canvas {
    display: block;
    width: 100%;
    max-width: 500px;
    cursor: crosshair;
    touch-action: none;
  }

  .sr-signature-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: #f7f9ff;
    border-top: 1px solid #eef0f6;
  }

  .sr-signature-hint {
    font-size: 12px;
    color: #9ca3af;
    font-style: italic;
  }

  .sr-signature-clear {
    font-size: 12px;
    color: #e05252;
    background: none;
    border: 1px solid #f0c0c0;
    border-radius: 6px;
    padding: 3px 10px;
    cursor: pointer;
  }

  /* Matrix */
  .sr-matrix-wrap {
    width: 100%;
    overflow-x: auto;
  }

  .sr-matrix-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }

  .sr-matrix-th {
    padding: 8px 12px;
    text-align: center;
    font-weight: 600;
    color: #4969ab;
    border-bottom: 2px solid #eef0f6;
    white-space: nowrap;
  }

  .sr-matrix-th:first-child { text-align: left; }

  .sr-matrix-row-even { background: #fafbff; }

  .sr-matrix-row-label {
    padding: 10px 12px;
    font-weight: 500;
    color: #1a2a4a;
    border-right: 1px solid #eef0f6;
    white-space: nowrap;
  }

  .sr-matrix-cell {
    padding: 10px;
    text-align: center;
    border-bottom: 1px solid #f0f2f8;
  }

  .sr-matrix-input {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #4969ab;
  }

  /* Ranking */
  .sr-ranking-hint {
    font-size: 12px;
    color: #9ca3af;
    margin-bottom: 10px;
    font-style: italic;
  }

  .sr-ranking-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 14px;
    border: 1.5px solid #dde0e8;
    border-radius: 10px;
    margin-bottom: 8px;
    background: #fafbff;
    cursor: grab;
    transition: all 0.18s;
    user-select: none;
  }

  .sr-ranking-item:hover { border-color: #4969ab; background: #eef3fc; }
  .sr-ranking-item.dragging { opacity: 0.5; border-style: dashed; }

  .sr-ranking-num {
    width: 24px; height: 24px;
    border-radius: 50%;
    background: #0a2352;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .sr-ranking-label { flex: 1; font-size: 14px; font-weight: 500; color: #1a2a4a; }

  .sr-ranking-handle { font-size: 18px; color: #c0c8da; cursor: grab; }

  /* Section heading */
  .sr-section-heading {
    margin-bottom: 4px;
    padding-top: 8px;
  }

  .sr-section-title {
    font-family: 'DM Serif Display', serif;
    font-size: 20px;
    font-weight: 400;
    color: #0a2352;
    margin: 0 0 4px;
  }

  .sr-section-desc {
    font-size: 13px;
    color: #6b7fa3;
    margin: 0 0 12px;
    line-height: 1.6;
  }

  .sr-section-divider {
    height: 2px;
    background: linear-gradient(to right, #4969ab, transparent);
    border-radius: 1px;
    margin-bottom: 4px;
  }

  /* Submit button */
  .sr-submit-area {
    padding-top: 24px;
    border-top: 1px solid #f0ede6;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
  }

  .sr-submit-btn {
    padding: 14px 40px;
    background: linear-gradient(135deg, #0a2352, #1a4fa0);
    color: #fff;
    border: none;
    border-radius: 12px;
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    letter-spacing: 0.02em;
  }

  .sr-submit-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(10,35,82,0.25);
  }

  .sr-submit-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  .sr-q-count {
    font-size: 12px;
    color: #9ca3af;
  }

  /* Error */
  .sr-error {
    background: #fff0f0;
    border: 1px solid #f0c0c0;
    border-radius: 10px;
    padding: 12px 16px;
    color: #b91c1c;
    font-size: 13px;
    margin-bottom: 16px;
  }

  /* States */
  .sr-loading, .sr-not-found, .sr-success {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    font-family: 'DM Sans', sans-serif;
    text-align: center;
    padding: 40px 20px;
  }

  .sr-success-icon {
    width: 80px; height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #0a2352, #1a4fa0);
    color: #fff;
    font-size: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    animation: scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  @keyframes scaleIn {
    from { transform: scale(0); opacity: 0; }
    to   { transform: scale(1); opacity: 1; }
  }

  .sr-success h2 {
    font-family: 'DM Serif Display', serif;
    font-size: 28px;
    color: #0a2352;
    margin: 0 0 8px;
  }

  .sr-success p {
    font-size: 15px;
    color: #6b7fa3;
    max-width: 320px;
    line-height: 1.6;
    margin: 0;
  }
`;

// ── Main renderer ─────────────────────────────────────────────────────────────
export default function SurveyRenderer() {
  const { slug }  = useParams();
  const [survey,   setSurvey]   = useState(null);
  const [answers,  setAnswers]  = useState({});
  const [status,   setStatus]   = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [loading,  setLoading]  = useState(true);

  useEffect(() => { fetchSurvey(); }, [slug]);

  const fetchSurvey = async () => {
    try {
      setLoading(true);

      const creds = Fetchexecutequery();

      const res = await axios.post(creds[1], {
        query: `
          SELECT id, title, description, slug
          FROM dynamic_form.questionnaires
          WHERE slug = '${slug}' AND is_active = 1
          LIMIT 1;
        `
      }, creds[0]);

      const qnr = res.data?.[0];
      if (!qnr) { setErrorMsg("Survey not found"); setLoading(false); return; }

      const qRes = await axios.post(creds[1], {
        query: `
          SELECT q.id, q.label, q.question_type, q.is_required, q.display_order,
                 q.config
          FROM dynamic_form.questions q
          WHERE q.questionnaire_id = ${qnr.id} AND q.is_active = 1
          ORDER BY q.display_order;
        `
      }, creds[0]);

      const questionRows = qRes.data || [];
      const questionIds = questionRows.map(q => q.id);

      let optionsByQuestion = {};
      if (questionIds.length) {
        const oRes = await axios.post(creds[1], {
          query: `
            SELECT id, question_id, option_label, option_value, display_order
            FROM dynamic_form.question_options
            WHERE is_active = 1
              AND question_id IN (${questionIds.join(",")})
            ORDER BY question_id, display_order;
          `
        }, creds[0]);

        optionsByQuestion = (oRes.data || []).reduce((acc, opt) => {
          if (!acc[opt.question_id]) acc[opt.question_id] = [];
          acc[opt.question_id].push({
            id: opt.id,
            option_label: opt.option_label,
            option_value: opt.option_value,
            display_order: opt.display_order,
          });
          return acc;
        }, {});
      }

      const questions = questionRows.map(q => ({
        id:          q.id,
        label:       q.label,
        type_code:   q.question_type,
        is_required: q.is_required,
        config:      (() => { const c = q.config; return c ? (typeof c === 'object' ? c : (() => { try { return JSON.parse(c); } catch { return {}; } })()) : {}; })(),
        options: optionsByQuestion[q.id] || [],
      }));

      setSurvey({ id: qnr.id, title: qnr.title, description: qnr.description, slug: qnr.slug, questions });

    } catch (err) {
      console.error("fetchSurvey error:", err);
      setErrorMsg("Failed to load survey");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    let remainingErrors = null;
    setValidationErrors(prev => {
      if (!prev[questionId]) return prev;
      const next = { ...prev };
      delete next[questionId];
      remainingErrors = Object.keys(next).length;
      return next;
    });
    if (remainingErrors === 0 && status === "error") {
      setErrorMsg("");
      setStatus("idle");
    }
    if (status === "error" && errorMsg === "Submission failed. Please try again.") {
      setErrorMsg("");
      setStatus("idle");
    }
  };

  const getPhoneDigits = (value) => {
    const phoneValue = typeof value === "string" ? value : "";
    return (phoneValue.split("|")[1] || "").replace(/\D/g, "");
  };

  const isValidEmail = (value) => {
    if (typeof value !== "string") return false;
    const email = value.trim();
    if (!email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidUrl = (value) => {
    if (typeof value !== "string") return false;
    const url = value.trim();
    if (!url) return false;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const isQuestionAnswered = (question, value) => {
    if (question.type_code === "section") return true;

    if (question.type_code === "checkbox" || question.type_code === "ranking") {
      return Array.isArray(value) && value.length > 0;
    }

    if (question.type_code === "matrix") {
      if (!value || typeof value !== "object") return false;
      const rows = question.config?.matrix_rows || question.matrix_rows || [];
      if (!rows.length) return Object.keys(value).length > 0;

      return rows.every((row) => {
        const rowValue = value[row];
        if (Array.isArray(rowValue)) return rowValue.length > 0;
        return rowValue !== undefined && rowValue !== null && String(rowValue).trim() !== "";
      });
    }

    if (question.type_code === "date_parts") {
      if (typeof value !== "string") return false;
      const [day, month, year] = value.split("|");
      return !!(day && month && year);
    }

    if (question.type_code === "phone") {
      if (typeof value !== "string") return false;
      const digits = getPhoneDigits(value);
      if (!digits) return false;
      const minDigits = question.config?.min_digits ? Number(question.config.min_digits) : null;
      const maxDigits = question.config?.max_digits ? Number(question.config.max_digits) : null;
      if (minDigits && digits.length < minDigits) return false;
      if (maxDigits && digits.length > maxDigits) return false;
      return true;
    }

    return value !== undefined && value !== null && String(value).trim() !== "";
  };

  const getQuestionValidationError = (question, value) => {
    if (question.type_code === "section") return "";

    if (question.type_code === "phone" && typeof value === "string") {
      const digits = getPhoneDigits(value);
      const minDigits = question.config?.min_digits ? Number(question.config.min_digits) : null;
      const maxDigits = question.config?.max_digits ? Number(question.config.max_digits) : null;
      if (!digits) {
        return question.is_required ? "This field is required." : "";
      }
      if (minDigits && maxDigits && (digits.length < minDigits || digits.length > maxDigits)) {
        return `Phone number must contain between ${minDigits} and ${maxDigits} digits.`;
      }
      if (minDigits && digits.length < minDigits) {
        return `Phone number must contain at least ${minDigits} digits.`;
      }
      if (maxDigits && digits.length > maxDigits) {
        return `Phone number must contain no more than ${maxDigits} digits.`;
      }
      if (question.is_required && !digits) {
        return "This field is required.";
      }
      return "";
    }

    if (question.type_code === "email") {
      if (!value || !String(value).trim()) {
        return question.is_required ? "This field is required." : "";
      }
      if (!isValidEmail(value)) {
        return "Invalid email address.";
      }
      return "";
    }

    if (question.type_code === "url") {
      if (!value || !String(value).trim()) {
        return question.is_required ? "This field is required." : "";
      }
      if (!isValidUrl(value)) {
        return "Invalid URL.";
      }
      return "";
    }

    if (question.type_code === "text" || question.type_code === "textarea") {
      const text = typeof value === "string" ? value.trim() : "";
      if (!text) {
        return question.is_required ? "This field is required." : "";
      }
      const minLength = question.config?.min_length ? Number(question.config.min_length) : null;
      const maxLength = question.config?.max_length ? Number(question.config.max_length) : null;
      if (minLength && text.length < minLength) {
        return `Please enter at least ${minLength} characters.`;
      }
      if (maxLength && text.length > maxLength) {
        return `Please enter no more than ${maxLength} characters.`;
      }
      return "";
    }

    if (question.type_code === "number") {
      if (value === undefined || value === null || String(value).trim() === "") {
        return question.is_required ? "This field is required." : "";
      }
      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) {
        return "Invalid number.";
      }
      const minValue = question.config?.min_value !== undefined && question.config?.min_value !== "" ? Number(question.config.min_value) : null;
      const maxValue = question.config?.max_value !== undefined && question.config?.max_value !== "" ? Number(question.config.max_value) : null;
      if (minValue !== null && numericValue < minValue) {
        return `Value must be at least ${minValue}.`;
      }
      if (maxValue !== null && numericValue > maxValue) {
        return `Value must be no more than ${maxValue}.`;
      }
      return "";
    }

    if (question.is_required && !isQuestionAnswered(question, value)) {
      return "This field is required.";
    }

    return "";
  };

  const answeredCount = Object.keys(answers).filter(
    k => {
      const v = answers[k];
      return v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0);
    }
  ).length;

  const totalQuestions = survey?.questions?.filter(q => q.type_code !== "section").length || 0;
  const progress       = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nextValidationErrors = {};
    let firstInvalidQuestion = null;

    survey?.questions?.forEach((question) => {
      const message = getQuestionValidationError(question, answers[question.id]);
      if (message) {
        nextValidationErrors[question.id] = message;
        if (!firstInvalidQuestion && question.type_code !== "section") {
          firstInvalidQuestion = question;
        }
      }
    });

    if (Object.keys(nextValidationErrors).length) {
      setValidationErrors(nextValidationErrors);
      setErrorMsg("");
      setStatus("error");
      if (firstInvalidQuestion) {
        const el = document.getElementById(`question-${firstInvalidQuestion.id}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          if (typeof el.focus === "function") el.focus();
        }
      }
      return;
    }

    setStatus("submitting");
    setValidationErrors({});
    try {
      const now = new Date().toISOString().slice(0, 19).replace("T", " ");

      const createCreds = CreateData();
      await axios.post(createCreds[1], {
        database: "dynamic_form",
        table: "responses",
        formData: { questionnaire_id: survey.id, slug: survey.slug, created_date: now, is_active: 1 },
      }, createCreds[0]);

      const fetchCreds = Fetchexecutequery();
      const responseRes = await axios.post(fetchCreds[1], {
        query: `SELECT id FROM dynamic_form.responses WHERE questionnaire_id = ${survey.id} ORDER BY id DESC LIMIT 1;`
      }, fetchCreds[0]);

      const responseId = responseRes.data?.[0]?.id;
      if (!responseId) throw new Error("Unable to create response");

      for (const questionId in answers) {
        let answerValue = answers[questionId];
        if (Array.isArray(answerValue)) answerValue = answerValue.join(",");
        if (typeof answerValue === "object") answerValue = JSON.stringify(answerValue);

        await axios.post(createCreds[1], {
          database: "dynamic_form",
          table: "response_answers",
          formData: {
            response_id:  responseId,
            question_id:  questionId,
            answer_text:  String(answerValue || ""),
            created_date: now,
            is_active:    1,
          },
        }, createCreds[0]);
      }

      setStatus("success");

    } catch (err) {
      console.error("Submission error:", err);
      setValidationErrors({});
      setErrorMsg("Submission failed. Please try again.");
      setStatus("error");
    }
  };

  // ── RENDER ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <>
        <style>{CSS}</style>
        <div className="sr-loading">
          <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
          <p style={{ color: "#6b7fa3", fontFamily: "DM Sans, sans-serif" }}>Loading survey…</p>
        </div>
      </>
    );
  }

  if (!survey) {
    return (
      <>
        <style>{CSS}</style>
        <div className="sr-not-found">
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
          <p style={{ color: "#6b7fa3", fontFamily: "DM Sans, sans-serif" }}>
            {errorMsg || "Survey not found."}
          </p>
        </div>
      </>
    );
  }

  if (status === "success") {
    return (
      <>
        <style>{CSS}</style>
        <div className="sr-page">
          <div className="sr-card">
            <div className="sr-success">
              <div className="sr-success-icon">✓</div>
              <h2>Thank you!</h2>
              <p>Your response has been recorded successfully. We appreciate your time.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{CSS}</style>
      <div className="sr-page">
        <div className="sr-card">

          {/* Header */}
          <div className="sr-header">
            <img src={sandhi} alt="Pontis" className="sr-header-logo"/>
            <h1>{survey.title}</h1>
            {survey.description && (
              <p>{survey.description}</p>
            )}
          </div>

          {/* Progress bar */}
          <div className="sr-progress">
            <div className="sr-progress-bar" style={{ width: `${progress}%` }} />
          </div>

          {/* Body */}
          <div className="sr-body">
            {status === "error" && (
              errorMsg ? <div className="sr-error">{errorMsg}</div> : null
            )}

            <form onSubmit={handleSubmit} noValidate>
              {survey.questions?.map((question, idx) => {
                const Component = QUESTION_COMPONENTS[question.type_code];
                if (!Component) return null;

                if (question.type_code === "section") {
                  return (
                    <div key={question.id} className="sr-question-block">
                      <Component question={question} value={answers[question.id]} onChange={handleChange} />
                    </div>
                  );
                }

                const qNum = survey.questions
                  .slice(0, idx + 1)
                  .filter(q => q.type_code !== "section").length;

                return (
                  <div key={question.id} id={`question-${question.id}`} tabIndex={-1} className={`sr-question-block ${validationErrors[question.id] ? "invalid" : ""}`}
                    style={{ animationDelay: `${idx * 0.04}s` }}>
                    <div className="sr-q-label">
                      <span className="sr-q-num">Q{qNum}</span>
                      <span>
                        {question.label}
                        {!!question.is_required && <span className="sr-required">*</span>}
                      </span>
                    </div>
                    <Component
                      question={question}
                      value={answers[question.id]}
                      onChange={handleChange}
                      hasError={!!validationErrors[question.id]}
                    />
                    {validationErrors[question.id] && (
                      <div style={{ marginTop: 8, color: "#b91c1c", fontSize: 13, fontWeight: 600 }}>
                        {validationErrors[question.id]}
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="sr-submit-area">
                <span className="sr-q-count">
                  {answeredCount} of {totalQuestions} answered
                </span>
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="sr-submit-btn"
                >
                  {status === "submitting" ? "Submitting…" : "Submit Response →"}
                </button>
              </div>
            </form>
          </div>

        </div>
      </div>
    </>
  );
}
